import React, { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { reviewsCopy, Review, ReviewsCopy } from '../data/translations/reviews';
const LOGO_ICON = 'https://cdn.magicpatterns.com/uploads/kWRktVv4YzBL2Ep7rCMN6b/logo-ster-transparant.png';
export function ReviewsSection() {
  const {
    language
  } = useLanguage();
  const copy = reviewsCopy[language];
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    setCanHover(query.matches);
    const onChange = (event: MediaQueryListEvent) => setCanHover(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return <section id="reviews" className="surface-b blend-top relative py-24 overflow-hidden" data-id="element-179" data-mp="nr15fe30xpk2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-180" data-mp="3lyefqttx2g8m">
        <div className="text-center mb-16" data-id="element-181" data-mp="3qpd0q6puo2i7">
          <div className="flex justify-center mb-4" data-id="element-182" data-mp="3qq4xhkv6o6a2">
            <img src={LOGO_ICON} alt="" className="w-12 h-12" data-id="element-183" data-mp="3cxyja4blc9d9" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink-900 mb-4" data-id="element-184" data-mp="3cvvj4rgq4zut">
            {copy.heading}
          </h2>
          <p className="text-bronze-600 text-lg font-medium tracking-wide uppercase" data-id="element-185" data-mp="8jqidil6hqa3">
            {copy.eyebrow}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-start" data-id="element-186" data-mp="dhrz3wv4tdie">
          {copy.reviews.map((review, index) => <ReviewCard key={index} review={review} copy={copy} canHover={canHover} data-id="element-187" data-mp="3vqsoi6jewn4a" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}
function ReviewCard({
  review,
  copy,
  canHover
}: {
  review: Review;
  copy: ReviewsCopy;
  canHover: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [tapped, setTapped] = useState(false);
  const expanded = canHover ? hovered : tapped;
  return <Card onMouseEnter={canHover ? () => setHovered(true) : undefined} onMouseLeave={canHover ? () => setHovered(false) : undefined} className="flex flex-col" data-id="element-188" data-mp="pv5jqljxs6ec">
      <Quote className="text-bronze-400 mb-4 opacity-60" size={32} data-id="element-189" data-mp="h090f06i5dcr" />

      <p className="font-serif text-xl md:text-2xl leading-snug mb-4 text-ink-900" data-id="element-190" data-mp="c1xpem4h0wsy">
        {review.keyQuote}
      </p>

      <p className="text-ink-600 text-base leading-relaxed" data-id="element-191" data-mp="70zzfnd7yygw">
        {review.shortText}
      </p>

      <AnimatePresence initial={false} data-id="element-192" data-mp="2405gatn9m65">
        {expanded && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-193" data-mp="3u7sjuzxfyab7">
            <div className="pt-4 mt-4 border-t border-line space-y-3" data-id="element-194" data-mp="3adsqlgf0prjx">
              {review.longText.split('\n\n').map((paragraph, index) => <p key={index} className="text-ink-600 text-sm leading-relaxed" data-id="element-195" data-mp="3fb2m9dt02zg7" data-mpi={index}>
                  {paragraph}
                </p>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="mt-6" data-id="element-196" data-mp="2rfg9n8hv7dds">
        <p className="text-ink-900 font-serif text-lg" data-id="element-197" data-mp="2rgj2vn2ntrwv">
          {review.author},{' '}
          <span className="text-bronze-600 text-base" data-id="element-198" data-mp="2web63d8r90gd">{review.role}</span>
        </p>

        {canHover ? <p className="mt-3 text-xs text-ink-600 uppercase tracking-widest" data-id="element-199" data-mp="30e1rf1fiiodi">
            {copy.hoverHint}
          </p> : <button type="button" onClick={() => setTapped(open => !open)} aria-expanded={tapped} className="mt-3 text-xs uppercase tracking-widest text-bronze-600 underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-400 rounded" data-id="element-200" data-mp="fp5oe3smki9t">
            {tapped ? copy.collapseLink : copy.expandLink}
          </button>}
      </div>
    </Card>;
}